<template>
  <div
    :class="[
      'w-full divide-y divide-background-fg/8 overflow-hidden rounded-lg bg-background',
      hasCurrencyMismatch
        ? 'border-l-4 border-amber-400'
        : '',
    ]"
  >
    <component
      :is="isTransactionClickable ? 'button' : 'div'"
      class="flex w-full items-center justify-between p-3"
      :type="isTransactionClickable ? 'button' : undefined"
      @click="
        isTransactionClickable &&
        emit('click-transaction', transaction)
      "
    >
      <div class="flex-1 space-y-1 text-left">
        <template
          v-if="transaction.status === 'successful'"
        >
          <PaymentDetailChip :payment="transaction" />
        </template>
        <template v-else>
          <p class="font-semibold text-error">
            <template
              v-if="transaction.status === 'cancelled'"
            >
              Cancelled
            </template>
            <template
              v-else-if="transaction.status === 'failed'"
            >
              Failed
            </template>
            <template
              v-else-if="transaction.status === 'voided'"
            >
              VOIDED
            </template>
          </p>
        </template>
        <div class="text-xs text-background-fg/70">
          {{ formattedDate }}
        </div>
      </div>
      <div class="flex flex-col items-end">
        <p class="font-semibold">
          {{
            formatToCurrencyString(
              transaction.amountTransactedTotal,
              transaction.currency
            )
          }}
        </p>
        <p
          v-if="
            transaction.transactionMethod === 'pos_card' &&
            transaction.isDcc &&
            transaction.isDccFieldsPopulated
          "
          class="text-xs font-semibold text-background-fg/70"
        >
          {{
            formatToCurrencyString(
              transaction.dccCardCurrencyAmount,
              transaction.dccCardholderCurrency
            )
          }}
        </p>

        <p
          v-if="transaction.amountCashback"
          class="text-xs text-background-fg/70"
        >
          (inc. cashback
          {{
            formatToCurrencyString(
              transaction.amountCashback,
              transaction.currency
            )
          }}
          )
        </p>
      </div>
      <div
        v-if="isTransactionClickable"
        class="flex w-6 items-center"
      >
        <IconChevronRight
          class="size-6 text-background-fg/20"
        />
      </div>
    </component>
    <template
      v-for="refund in transaction.refunds"
      :key="refund.id"
    >
      <component
        :is="isRefundClickable(refund) ? 'button' : 'div'"
        class="flex w-full items-center justify-between p-3"
        :type="
          isRefundClickable(refund) ? 'button' : undefined
        "
        @click="
          isRefundClickable(refund) &&
          emit('click-transaction', refund)
        "
      >
        <div class="flex-1 space-y-1 text-left">
          <p class="text-sm font-semibold">
            Refund
            <span
              v-if="refund.status === 'voided'"
              class="text-error"
            >
              (VOIDED)
            </span>
          </p>
          <div class="text-xs text-background-fg/70">
            {{
              DateFns.format(
                refund.createdAt,
                "HH:mm dd/MM/yy"
              )
            }}
          </div>
        </div>
        <div class="flex flex-col items-end">
          <p class="text-sm font-semibold text-error">
            -
            {{
              formatToCurrencyString(
                refund.amountTransactedTotal,
                refund.currency
              )
            }}
          </p>
          <p
            v-if="
              refund.transactionMethod === 'pos_card' &&
              refund.isDcc &&
              refund.isDccFieldsPopulated
            "
            class="text-xs font-semibold text-error/70"
          >
            -
            {{
              formatToCurrencyString(
                refund.dccCardCurrencyAmount,
                refund.dccCardholderCurrency
              )
            }}
          </p>
        </div>
        <div
          v-if="isRefundClickable(refund)"
          class="flex w-6 items-center"
        >
          <IconChevronRight
            class="size-6 text-background-fg/20"
          />
        </div>
      </component>
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  OrderDetailPayment,
  OrderDetailRefund,
} from "@/services/orders/retrieveOrder"
import * as DateFns from "date-fns"

const props = defineProps<{
  transaction: OrderDetailPayment
}>()

const emit = defineEmits<{
  (
    event: "click-transaction",
    params: OrderDetailPayment | OrderDetailRefund
  ): void
}>()

const { formatToCurrencyString } = useCurrency()
const { currency: merchantCurrency } = useTenant()

const formattedDate = computed(() =>
  DateFns.format(
    props.transaction.createdAt,
    "HH:mm dd/MM/yy"
  )
)

const hasCurrencyMismatch = computed(
  () =>
    props.transaction.currency !== merchantCurrency.value ||
    props.transaction.refunds.some(
      (r) => r.currency !== merchantCurrency.value
    )
)

const isTransactionClickable = computed(
  () =>
    !["cancelled", "pending"].includes(
      props.transaction.status
    )
)
const isRefundClickable = (r: OrderDetailRefund) =>
  !["cancelled", "pending"].includes(r.status)
</script>
